/* eslint-disable react-hooks/exhaustive-deps */
import {
  Autocomplete,
  Popper,
  TextField,
  autocompleteClasses,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { ElementType, useEffect, useState } from 'react';
import { Noop } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { toast } from 'react-toastify';
import ApplicationSchema from 'src/configs/APISchemas/Application.schema';
import { GET_ORGANISATION_BUILDING_BY_NAME } from 'src/configs/APISchemas/BuildingByOrganisationName';
import { GraphQLRequest } from 'src/helpers/GraphQLRequest';
import { UpdateApplication } from 'src/redux/actions/Application';
import { Store } from 'src/types';
import MuiListbox from './MuiListbox';

const CustomPopper = (props: any) => {
  return (
    <Popper
      {...props}
      placement="bottom"
      disablePortal={true}
      modifiers={[
        {
          name: 'flip',
          enabled: false,
          options: {
            altBoundary: true,
            rootBoundary: 'viewport',
            padding: 8,
          },
        },
        {
          name: 'preventOverflow',
          enabled: false,
          options: {
            altAxis: true,
            altBoundary: true,
            tether: true,
            rootBoundary: 'viewport',
            padding: 8,
          },
        },
        {
          name: 'arrow',
          enabled: false,
        },
      ]}
    />
  );
};

const StyledPopper: ElementType = styled(CustomPopper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    border: '1px solid #1D1D1D',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

interface CompanyDropdown {
  value: string | undefined;
  placeholder: string;
  onChange: (
    selectedAddress: ApplicationSchema['buildingAddress']['value'],
  ) => void;
  setPageLoading: React.Dispatch<React.SetStateAction<boolean>>;
  onBlur: Noop;
}

const CompanyDropdown = ({
  value,
  placeholder,
  onChange,
  setPageLoading,
  onBlur,
}: CompanyDropdown) => {
  const application = useSelector((store: Store) => store.application);
  const dispatch = useDispatch();
  const history = useHistory();
  const [selectedOption, setSelectedOption] = useState({
    value: undefined,
    label: undefined,
  });
  const [listOfCompanyAddresses, setListOfCompanyAddresses] = useState([]);

  useEffect(() => {
    getListOfCompanyBuildingsAddresses();
  }, []);

  /* SECTION 0 : QUERIES */
  const getListOfCompanyBuildingsAddresses = async () => {
    setPageLoading(true);
    const COMPANY_VENDOR = application.data.COMPANY_VENDOR || 'organisation';
    try {
      const query = GET_ORGANISATION_BUILDING_BY_NAME(COMPANY_VENDOR);
      const response = await GraphQLRequest(query);
      if (response?.buildingByOrganisationName?.buildings) {
        const buildings = response.buildingByOrganisationName.buildings.map(
          (b: any) => ({
            name: b.name,
            id: b.id,
            addressNumber: b.fullAddress.addressNumber,
            street: b.fullAddress.street,
            municipality: b.fullAddress.municipality,
            state: b.fullAddress.region,
            postalCode: b.fullAddress.postalCode,
            complete_address:
              b.fullAddress.addressNumber ||
              '' +
                ' ' +
                b.fullAddress.street +
                ', ' +
                b.fullAddress.municipality +
                ', ' +
                b.fullAddress.region +
                ', ' +
                b.fullAddress.postalCode,
          }),
        );
        setListOfCompanyAddresses(buildings);
        const selectedAddress = buildings.find((add: any) => add.id === value);
        setSelectedOption({
          value: selectedAddress?.buildingId || '',
          label: selectedAddress?.name || '',
        });
        setPageLoading(false);
      } else {
        toast('Organization not Found', { type: 'error' });
        handleCantFindCompanyList();
        history.push('/compliance/welcome');
        return;
      }
    } catch (error: any) {
      setPageLoading(false);
      if (error?.response?.data?.errors) {
        return toast(error.response.data.errors[0].message, {
          type: 'error',
        });
      }
      toast('We are having some trouble, Please contact support for help.', {
        type: 'error',
      });
    }
  };
  /* END OF SECTION 0 */

  const onAddressSelection = (
    _: any,
    selectedOption: { label: string | undefined; value: string | undefined },
  ) => {
    if (!selectedOption || !selectedOption.label || !selectedOption.value) {
      return;
    }

    const value = selectedOption.value;
    const selectedAddress = listOfCompanyAddresses.find(
      (add: any) => add.id === value,
    );

    selectedAddress && onChange(selectedAddress);
  };

  const handleCantFindCompanyList = () => {
    dispatch(
      UpdateApplication({
        ...application.data,
        buildingName: undefined,
        buildingId: undefined,
        addressName: undefined,
        buildingAddress: undefined,
        address: undefined,
        COMPANY_VENDOR: undefined,
      }),
    );
  };

  return (
    <>
      {selectedOption.label != undefined && (
        <Autocomplete
          defaultValue={selectedOption}
          disableClearable={true}
          onBlur={onBlur}
          noOptionsText="No Option Found"
          disableListWrap
          PopperComponent={StyledPopper}
          ListboxComponent={MuiListbox}
          options={
            listOfCompanyAddresses?.map((address: any) => ({
              value: address.id,
              label: address.name,
              address: address.complete_address,
              city: address.municipality,
            })) || [{ value: '', label: 'No Option found', address: '' }]
          }
          filterOptions={(options: any, { inputValue }) => {
            return options.filter(
              (option: any) =>
                option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                option.address.toLowerCase().includes(inputValue.toLowerCase()),
            );
          }}
          getOptionLabel={(option) => (option.label ? option.label : '')}
          onChange={onAddressSelection}
          renderInput={(params) => (
            <TextField {...params} label={placeholder} required />
          )}
          renderOption={(props, option, state) =>
            [props, option, state.index] as React.ReactNode
          }
          // TODO: Post React 18 update - validate this conversion, look like a hidden bug
          renderGroup={(params) => params as unknown as React.ReactNode}
        />
      )}
    </>
  );
};

export default CompanyDropdown;
